import { flatten } from 'utils';
import messagesInEnglish from '../../lang/en.json';
import messagesInBritishEnglish from '../../lang/en-GB.json';
import messagesInCanadianEnglish from '../../lang/en-CA.json';
import messagesInIrishEnglish from '../../lang/en-IE.json';
import messagesInAustralianEnglish from '../../lang/en-AU.json';
import messagesInGerman from '../../lang/de-DE.json';
import messagesInSpanish from '../../lang/es-ES.json';
import messagesInFrench from '../../lang/fr-FR.json';
import messagesInCanadianFrench from '../../lang/fr-CA.json';
import messagesInItalian from '../../lang/it-IT.json';
import messagesInNorwegian from '../../lang/nb-NO.json';
import messagesInDutch from '../../lang/nl-NL.json';
import messagesInPolish from '../../lang/pl.json';
import messagesInPortuguese from '../../lang/pt-PT.json';
import messagesInHungarian from '../../lang/hu-HU.json';
import messagesInSlovak from '../../lang/sk-SK.json';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

// Keys are lower-case hyphenated so the normalized lookup in getAvailableLocale hits regardless of the
// host's casing/separator. Each single-region language also carries a bare-language alias (`de`, `fr`,
// …) so a language-only tag from the host still resolves instead of falling to English. en-GB is the
// British dialect parent en-IE / en-AU derive from; bare `fr` maps to fr-FR (not fr-CA).
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.packageManagement],
  ['en-gb', messagesInBritishEnglish.packageManagement],
  ['en-ca', messagesInCanadianEnglish.packageManagement],
  ['en-ie', messagesInIrishEnglish.packageManagement],
  ['en-au', messagesInAustralianEnglish.packageManagement],
  ['de-de', messagesInGerman.packageManagement],
  ['de', messagesInGerman.packageManagement],
  ['es-es', messagesInSpanish.packageManagement],
  ['es', messagesInSpanish.packageManagement],
  ['fr-fr', messagesInFrench.packageManagement],
  ['fr', messagesInFrench.packageManagement],
  ['fr-ca', messagesInCanadianFrench.packageManagement],
  ['it-it', messagesInItalian.packageManagement],
  ['it', messagesInItalian.packageManagement],
  ['nb-no', messagesInNorwegian.packageManagement],
  ['nb', messagesInNorwegian.packageManagement],
  ['nl-nl', messagesInDutch.packageManagement],
  ['nl', messagesInDutch.packageManagement],
  ['pl', messagesInPolish.packageManagement],
  ['pt-pt', messagesInPortuguese.packageManagement],
  ['pt', messagesInPortuguese.packageManagement],
  ['hu-hu', messagesInHungarian.packageManagement],
  ['hu', messagesInHungarian.packageManagement],
  ['sk-sk', messagesInSlovak.packageManagement],
  ['sk', messagesInSlovak.packageManagement],
]);

/**
 * Strip POSIX codeset (`.UTF-8`) and modifier (`@euro`) suffixes before BCP-47 normalization.
 * A host may pass `hu_HU.UTF-8` or `de_DE@euro`; neither `.UTF-8` nor `@euro` is BCP-47, and if
 * the suffix survives into the locale handed to `IntlProvider` it throws a `RangeError` in every
 * `Intl` formatter. Strip at the gate so the rest of the pipeline only sees clean tags.
 */
const stripPosixSuffix = (tag: string): string => tag.replace(/[.@].*$/, '');

export const getAvailableLocale = (userLocale: string): [string, string] => {
  // Strip POSIX suffixes FIRST, then normalize case + separator. Without the strip, `hu_HU.UTF-8`
  // survives as `hu-hu.utf-8` — resolves the catalogue via the bare-language alias, but the returned
  // locale tag still carries `.utf-8` and throws RangeError in Intl.DateTimeFormat (VIS-1951).
  const normalized = stripPosixSuffix(userLocale).toLowerCase().replace(/_/g, '-');

  // Return the NORMALIZED tag, not the caller's. It becomes the IntlProvider locale and therefore the
  // argument to every Intl formatter, and an underscored tag is not valid BCP-47 — `hu_HU` throws a
  // RangeError in Intl.NumberFormat / Intl.DateTimeFormat. Handing back the raw input would resolve the
  // catalogue and then crash on the first formatted number, which is worse than the English fallback it
  // replaced. Intl is case-insensitive, so the lower-cased form is safe.
  if (messagesMap.has(normalized)) {
    return [normalized, normalized];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    return [lang, normalized];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => useMemo(() => {
  const [messagesKey, locale] = getAvailableLocale(userLocale);

  return {
    locale,
    messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
  };
}, [userLocale]);
